'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Badge from '@/components/ui/Badge';
import { Trophy, Star, BookOpen, MessageCircle, Filter, Users, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { getLeaderboard } from '@/lib/db/profiles';
import PodiumSection from './PodiumSection';
import type { LeaderboardEntry } from './leaderboardData';

const tierLabel: Record<string, string> = {
  new: 'New',
  trusted: 'Trusted',
  historian: 'Historian',
  elder: 'Elder',
};

const tierColors: Record<string, string> = {
  new: 'var(--muted-foreground)',
  trusted: '#008080',
  historian: '#E05A47',
  elder: '#F39C12',
};

const eras = [
  'All Eras',
  'Early Iron Age',
  'Kingdom of Mutapa',
  'Rozvi Empire',
  'Colonial Contact',
  'Anti Colonial Resistance',
  'Nationalist Period',
  'Post-Independence',
];

const tiers = ['All Tiers', 'new', 'trusted', 'historian', 'elder'] as const;
type TierFilter = typeof tiers[number];

type SortKey = 'reputation' | 'contributions' | 'corroborations' | 'eraFocus';
type SortDir = 'asc' | 'desc';

function SortIcon({ col, sortBy, sortDir }: { col: SortKey; sortBy: SortKey; sortDir: SortDir }) {
  if (sortBy !== col) return <ChevronsUpDown size={12} style={{ color: 'var(--muted-foreground)', opacity: 0.5 }} />;
  return sortDir === 'desc'
    ? <ChevronDown size={12} style={{ color: 'var(--primary)' }} />
    : <ChevronUp size={12} style={{ color: 'var(--primary)' }} />;
}

export default function LeaderboardPage() {
  const [eraFilter, setEraFilter] = useState('All Eras');
  const [tierFilter, setTierFilter] = useState<TierFilter>('All Tiers');
  const [sortBy, setSortBy] = useState<SortKey>('reputation');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getLeaderboard();
      setLeaderboardData(data as LeaderboardEntry[]);
    } catch (err: any) {
      console.error('loadLeaderboard error:', err.message);
      setError('Failed to load leaderboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (col: SortKey) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  const filtered = leaderboardData
    .filter((e) => eraFilter === 'All Eras' || e.eraFocus === eraFilter)
    .filter((e) => tierFilter === 'All Tiers' || e.tier === tierFilter)
    .sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'reputation') cmp = b.reputationScore - a.reputationScore;
      else if (sortBy === 'contributions') cmp = b.publishedCount - a.publishedCount;
      else if (sortBy === 'corroborations') cmp = b.corroborationsReceived - a.corroborationsReceived;
      else if (sortBy === 'eraFocus') cmp = (a.eraFocus || '').localeCompare(b.eraFocus || '');
      return sortDir === 'asc' ? -cmp : cmp;
    });

  const top3 = leaderboardData.slice(0, 3);

  const sortableColStyle = (col: SortKey): React.CSSProperties => ({
    color: sortBy === col ? 'var(--primary)' : 'var(--muted-foreground)',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  });

  return (
    <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 py-8">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Users size={20} style={{ color: 'var(--primary)' }} />
            <h1 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>
              Community Leaderboard
            </h1>
          </div>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            Top contributors ranked by reputation score. Click column headers to sort by different signals.
          </p>
        </div>

        <div
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border"
          style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
        >
          <Trophy size={16} style={{ color: 'var(--primary)' }} />
          <div>
            <div className="text-lg font-bold tabular-nums leading-none" style={{ color: 'var(--primary)' }}>
              {loading ? '…' : leaderboardData.length}
            </div>
            <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Active contributors
            </div>
          </div>
        </div>
      </div>

      {/* Podium */}
      <PodiumSection top3={top3} />

      {/* Filters */}
      <div className="flex flex-col gap-3 mb-5">
        {/* Era filter */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter size={14} style={{ color: 'var(--muted-foreground)' }} />
            <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>
              Era:
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {eras.map((era) => (
              <button
                key={`era-filter-lb-${era}`}
                onClick={() => setEraFilter(era)}
                className="px-2.5 py-1 rounded-lg text-xs font-medium transition-all duration-150 border"
                style={{
                  backgroundColor: eraFilter === era ? 'var(--primary)' : 'transparent',
                  color: eraFilter === era ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
                  borderColor: eraFilter === era ? 'var(--primary)' : 'var(--border)',
                }}
              >
                {era}
              </button>
            ))}
          </div>
        </div>

        {/* Trust tier filter */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Star size={14} style={{ color: 'var(--muted-foreground)' }} />
            <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>
              Trust tier:
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {tiers.map((t) => {
              const isActive = tierFilter === t;
              const dotColor = t !== 'All Tiers' ? tierColors[t] : undefined;
              return (
                <button
                  key={`tier-filter-${t}`}
                  onClick={() => setTierFilter(t)}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all duration-150 border"
                  style={{
                    backgroundColor: isActive
                      ? t === 'All Tiers' ?'var(--foreground)'
                        : dotColor + '22' :'transparent',
                    color: isActive
                      ? t === 'All Tiers' ?'var(--background)'
                        : dotColor
                      : 'var(--muted-foreground)',
                    borderColor: isActive
                      ? t === 'All Tiers' ?'var(--foreground)'
                        : dotColor
                      : 'var(--border)',
                  }}
                >
                  {t !== 'All Tiers' && (
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: dotColor }}
                    />
                  )}
                  {t === 'All Tiers' ? 'All Tiers' : tierLabel[t]}
                </button>
              );
            })}
          </div>

          {/* Active filter count badge */}
          {(eraFilter !== 'All Eras' || tierFilter !== 'All Tiers') && (
            <button
              onClick={() => { setEraFilter('All Eras'); setTierFilter('All Tiers'); }}
              className="ml-auto text-xs px-2.5 py-1 rounded-lg border transition-colors duration-150"
              style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div
        className="rounded-xl border overflow-hidden"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3">
            <div
              className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'var(--primary)' }}
            />
            <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              Loading leaderboard…
            </span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{error}</p>
            <button
              onClick={loadLeaderboard}
              className="text-xs px-3 py-1.5 rounded-lg border transition-colors duration-150"
              style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {/* Non-sortable */}
                  {['Rank', 'Contributor', 'Tier'].map((col) => (
                    <th
                      key={`lb-col-${col}`}
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide"
                      style={{ color: 'var(--muted-foreground)' }}
                    >
                      {col}
                    </th>
                  ))}

                  {/* Sortable: Reputation */}
                  <th
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide"
                    style={sortableColStyle('reputation')}
                    onClick={() => handleSort('reputation')}
                  >
                    <span className="flex items-center gap-1">
                      Reputation
                      <SortIcon col="reputation" sortBy={sortBy} sortDir={sortDir} />
                    </span>
                  </th>

                  {/* Sortable: Contributions */}
                  <th
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide"
                    style={sortableColStyle('contributions')}
                    onClick={() => handleSort('contributions')}
                  >
                    <span className="flex items-center gap-1">
                      Published
                      <SortIcon col="contributions" sortBy={sortBy} sortDir={sortDir} />
                    </span>
                  </th>

                  {/* Sortable: Corroborations */}
                  <th
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide"
                    style={sortableColStyle('corroborations')}
                    onClick={() => handleSort('corroborations')}
                  >
                    <span className="flex items-center gap-1">
                      Corroborations
                      <SortIcon col="corroborations" sortBy={sortBy} sortDir={sortDir} />
                    </span>
                  </th>

                  {/* Sortable: Era Focus */}
                  <th
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide"
                    style={sortableColStyle('eraFocus')}
                    onClick={() => handleSort('eraFocus')}
                  >
                    <span className="flex items-center gap-1">
                      Era Focus
                      <SortIcon col="eraFocus" sortBy={sortBy} sortDir={sortDir} />
                    </span>
                  </th>

                  {/* Non-sortable */}
                  {['Last Active', ''].map((col) => (
                    <th
                      key={`lb-col-${col}`}
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide"
                      style={{ color: 'var(--muted-foreground)' }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <Users size={28} style={{ color: 'var(--muted-foreground)' }} />
                        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                          No contributors match the current filters
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((entry, idx) => (
                    <tr
                      key={entry.id}
                      className="transition-colors duration-100"
                      style={{
                        borderBottom: idx < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'var(--muted)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent';
                      }}
                    >
                      {/* Rank */}
                      <td className="px-4 py-3.5">
                        <span
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-sm font-bold tabular-nums"
                          style={{
                            backgroundColor:
                              entry.rank === 1 ? '#D4A84733' :
                              entry.rank === 2 ? '#6B6B6B33' :
                              entry.rank === 3 ? '#8B5E3C33' : 'var(--muted)',
                            color:
                              entry.rank === 1 ? '#E8C86A' :
                              entry.rank === 2 ? '#AAAAAA' :
                              entry.rank === 3 ? '#C17A3E' : 'var(--muted-foreground)',
                          }}
                        >
                          {entry.rank}
                        </span>
                      </td>

                      {/* Contributor */}
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm flex-shrink-0"
                            style={{
                              backgroundColor: tierColors[entry.tier] + '33',
                              color: tierColors[entry.tier],
                            }}
                          >
                            {entry.displayName?.[0] || '?'}
                          </div>
                          <div>
                            <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
                              {entry.displayName}
                            </p>
                            <p
                              className="text-xs"
                              style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)', fontSize: '0.7rem' }}
                            >
                              @{entry.username}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Tier */}
                      <td className="px-4 py-3.5">
                        <Badge
                          variant={`tier-${entry.tier}` as 'tier-new' | 'tier-trusted' | 'tier-historian' | 'tier-elder'}
                          label={tierLabel[entry.tier]}
                        />
                      </td>

                      {/* Reputation */}
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <Star size={12} style={{ color: tierColors[entry.tier] }} />
                          <span
                            className="text-sm font-bold tabular-nums"
                            style={{ color: tierColors[entry.tier] }}
                          >
                            {entry.reputationScore}
                          </span>
                        </div>
                      </td>

                      {/* Published */}
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <BookOpen size={12} style={{ color: 'var(--muted-foreground)' }} />
                          <span className="text-sm tabular-nums" style={{ color: 'var(--foreground)' }}>
                            {entry.publishedCount}
                          </span>
                        </div>
                      </td>

                      {/* Corroborations */}
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <MessageCircle size={12} style={{ color: 'var(--muted-foreground)' }} />
                          <span className="text-sm tabular-nums" style={{ color: 'var(--foreground)' }}>
                            {entry.corroborationsReceived}
                          </span>
                        </div>
                      </td>

                      {/* Era Focus */}
                      <td className="px-4 py-3.5">
                        <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                          {entry.eraFocus || '—'}
                        </span>
                      </td>

                      {/* Last Active */}
                      <td className="px-4 py-3.5">
                        <span className="text-xs tabular-nums" style={{ color: 'var(--muted-foreground)' }}>
                          {entry.lastActive}
                        </span>
                      </td>

                      {/* Action */}
                      <td className="px-4 py-3.5">
                        <Link
                          href="/contributor-profile"
                          className="text-xs px-2.5 py-1 rounded-lg border transition-colors duration-150"
                          style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}